import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { contactService } from '../services/contactService';
import { 
  Search, 
  User, 
  Check,
  X
} from 'lucide-react';

const ContactSelector = ({ 
  onSelectContact, 
  selectedContacts = [], 
  placeholder = "Search contacts...",
  allowMultiple = false,
  className = "" 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Fetch contacts based on search term
  const { data: contactsData, isLoading } = useQuery(
    ['contacts', searchTerm],
    () => contactService.getContacts(1, 20, searchTerm),
    {
      enabled: showDropdown && searchTerm.length >= 0,
      staleTime: 30000,
    }
  );

  const contacts = contactsData?.contacts || [];

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const handleSelectContact = (contact) => {
    onSelectContact(contact);
    
    if (!allowMultiple) {
      setInputValue(contact.email);
      setShowDropdown(false);
    } else {
      setInputValue('');
      setSearchTerm('');
    }
  };

  const handleRemoveContact = (contactId) => {
    if (allowMultiple && onSelectContact) {
      // For multiple selection, we need a way to remove contacts
      // This would need to be handled by the parent component
      const remainingContacts = selectedContacts.filter(c => c.id !== contactId);
      onSelectContact(remainingContacts);
    }
  };

  const isContactSelected = (contact) => {
    return selectedContacts.some(selected => selected.id === contact.id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.contact-selector')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`contact-selector relative ${className}`}>
      {/* Selected Contacts (for multiple selection) */}
      {allowMultiple && selectedContacts.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedContacts.map(contact => (
            <div
              key={contact.id}
              className="px-3 py-1 rounded-full text-sm flex items-center gap-2 border"
              style={{ background: 'rgba(30, 64, 175, 0.08)', color: '#1e40af', borderColor: 'rgba(148, 163, 184, 0.2)' }}
            >
              <span>{contact.name} ({contact.email})</span>
              <button
                onClick={() => handleRemoveContact(contact.id)}
                className="text-blue-600 hover:text-blue-800"
                aria-label="Remove contact"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" aria-hidden="true" />
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] shadow-sm"
          aria-label="Search contacts"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute z-50 w-full mt-1 rounded-2xl shadow-lg max-h-60 overflow-y-auto border"
          style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(249, 250, 251, 0.95))', borderColor: 'rgba(148, 163, 184, 0.2)' }}
          role="listbox"
          aria-label="Contacts"
        >
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mx-auto" aria-hidden="true"></div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm ? (
                <div>
                  <p className="mb-2">No contacts found for "{searchTerm}"</p>
                  <p className="text-sm">You can still enter the email manually</p>
                </div>
              ) : (
                <div>
                  <User className="w-8 h-8 mx-auto mb-2 text-gray-400" aria-hidden="true" />
                  <p>No contacts yet</p>
                  <p className="text-sm">Add contacts to select them quickly</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              {contacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => handleSelectContact(contact)}
                  className={`w-full p-3 text-left transition-all border-b last:border-b-0 ${
                    isContactSelected(contact) ? 'bg-blue-50 border-blue-100' : 'hover:bg-gray-50 border-gray-100'
                  }`}
                  role="option"
                  aria-selected={isContactSelected(contact)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(30, 64, 175, 0.08)' }}>
                      <span className="text-blue-700 text-sm font-semibold">
                        {contact.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-gray-900 font-medium truncate">
                          {contact.name}
                        </p>
                        {isContactSelected(contact) && (
                          <Check className="w-4 h-4 text-blue-600 flex-shrink-0" aria-hidden="true" />
                        )}
                      </div>
                      <p className="text-gray-600 text-sm truncate">
                        {contact.email}
                      </p>
                      {contact.company && (
                        <p className="text-gray-500 text-xs truncate">
                          {contact.company}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactSelector; 